import { MorphoVault, MorphoVaults } from './types'

/**
 * This file guards ONE Morpho-specific mechanism (the pinned-market runaway).
 * The provider-agnostic checks — annualization noise near a fixed-term
 * maturity, and an implausible magnitude on a dust vault — live in
 * `../rateSanity.ts`, which also records why a NEGATIVE Morpho rate must
 * never be clamped: the forward `netApy` this provider reads is
 * non-negative by construction, but a realized field would go negative
 * exactly when the vault socializes bad debt, and that is the one number a
 * depositor must not be shielded from.
 */

/**
 * APR (percent) above which a MetaMorpho rate stops being a yield and starts
 * being an artifact.
 *
 * Nothing on the Morpho catalogue pays this — the highest genuine rate across
 * every chain we serve sits well under 50 % APR. Rows above the ceiling are
 * always the same mechanism (see {@link isUnrealizableRate}), never a real
 * opportunity, so the number only has to sit above real yields and below the
 * runaway band, which starts in the hundreds of percent.
 */
const IMPLAUSIBLE_APR_PERCENT = 100

/**
 * Withdrawable fraction of the vault below which its allocations count as
 * PINNED — i.e. the markets it lends into are at ~100 % utilization.
 *
 * A tenth of a percent is far below anything a working vault sits at while
 * still being orders of magnitude above the affected rows, which hold sub-
 * dollar liquidity against six- to nine-figure TVL ($0.76 withdrawable on a
 * $160.8M vault).
 */
const PINNED_LIQUIDITY_FRACTION = 1e-3

/**
 * True when the vault's advertised rate cannot be earned by a depositor.
 *
 * Morpho Blue's adaptive IRM ratchets its rate upward for as long as a market
 * stays above target utilization, with no ceiling. A market pinned at 100 %
 * therefore publishes an ever-growing `supplyApy` — 297,996 % on the markets
 * behind these rows, which `apyToAprPercent` compresses to a tidy-looking
 * 800.00 % APR — and the vault-level `netApy` inherits it whole, because the
 * pinned market is where ~100 % of the vault sits.
 *
 * **The two halves are the same fact.** A market at 100 % utilization has
 * lent out everything, so the runaway rate arrives together with nothing to
 * withdraw: every affected row on every chain carries under a dollar of
 * liquidity, and the rate is a penalty charged to a borrower who is usually
 * the depositor themselves — a closed loop, not a yield anyone can enter.
 * That is why the gate tests BOTH: a high rate alone could be a genuine
 * incentive campaign, and zero liquidity alone is ordinary for a fully-lent
 * vault (8 chain-1 vaults sit there at sane rates). Only together do they
 * identify the pin.
 *
 * Rows that match are dropped rather than re-rated, because their rate is not
 * the only thing that is wrong with them as an earn listing: the same pin
 * means a depositor could enter and not exit.
 */
export const isUnrealizableRate = (v: MorphoVault): boolean =>
  isPinnedMarketRate({
    aprPercent: v.depositRate,
    totalAssets: v.totalAssetsFormatted,
    liquidity: v.liquidityFormatted,
  })

/**
 * The same predicate, on loose fields.
 *
 * Exists because the mechanism is NOT Morpho-specific — the earn surface hits
 * it on Silo and on plain Morpho Blue LENDING markets, neither of which is a
 * `MorphoVault` — and one implementation with two doors beats two
 * implementations that drift. See `earn/rateSanity.ts`.
 */
export const isPinnedMarketRate = (args: {
  aprPercent: number
  totalAssets: number
  liquidity: number
}): boolean => {
  if (!(args.aprPercent > IMPLAUSIBLE_APR_PERCENT)) return false
  if (!(args.totalAssets > 0)) return false
  return args.liquidity / args.totalAssets < PINNED_LIQUIDITY_FRACTION
}

/**
 * Drops vaults whose rate is an artifact of a pinned market — see
 * {@link isUnrealizableRate}. Returns the same object when nothing matches so
 * the common path allocates nothing.
 */
export const dropUnrealizableRates = (vaults: MorphoVaults): MorphoVaults => {
  const entries = Object.entries(vaults)
  const kept = entries.filter(([, v]) => !isUnrealizableRate(v))
  if (kept.length === entries.length) return vaults
  return Object.fromEntries(kept)
}
